import type { SharePlainPayload } from './sharePayload';

/** Thrown when decrypted share text looks like HTML/script injection. */
export class ShareContentBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShareContentBlockedError';
  }
}

const HTML_TAG_RE =
  /<\s*\/?\s*(script|iframe|object|embed|link|style|meta|base|form|svg|math|template|body|html|head|img|video|audio|source|track|portal|frame)\b/i;
const GENERIC_TAG_RE = /<\s*\/?\s*[a-zA-Z][\w-]*\b/;
const EVENT_HANDLER_RE = /\bon[a-z]+\s*=/i;
const JS_SCHEME_RE = /javascript\s*:/i;
const DATA_HTML_RE = /data\s*:\s*text\/html/i;
const NULL_BYTE_RE = /\u0000/;

/** Returns a short reason if `text` must not be shown on the decrypt page. */
export function detectUnsafeShareText(text: string): string | null {
  if (NULL_BYTE_RE.test(text)) return 'Invalid characters in shared content.';
  if (HTML_TAG_RE.test(text)) return 'HTML tags are not allowed in shared content.';
  if (GENERIC_TAG_RE.test(text)) return 'HTML markup is not allowed in shared content.';
  if (EVENT_HANDLER_RE.test(text)) return 'Script event handlers are not allowed in shared content.';
  if (JS_SCHEME_RE.test(text)) return 'JavaScript URLs are not allowed in shared content.';
  if (DATA_HTML_RE.test(text)) return 'Embedded HTML data URLs are not allowed in shared content.';
  return null;
}

export function assertSharePayloadSafe(payload: SharePlainPayload): SharePlainPayload {
  const titleIssue = detectUnsafeShareText(payload.title);
  if (titleIssue) throw new ShareContentBlockedError(titleIssue);
  const bodyIssue = detectUnsafeShareText(payload.body);
  if (bodyIssue) throw new ShareContentBlockedError(bodyIssue);
  return payload;
}

const SHARE_CSP_META_ID = 'share-decrypt-csp';

/** Lock down the decrypt-only page so injected markup cannot run scripts. */
export function applyShareDecryptContentSecurityPolicy(): void {
  if (document.getElementById(SHARE_CSP_META_ID)) return;
  const meta = document.createElement('meta');
  meta.id = SHARE_CSP_META_ID;
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = [
    "default-src 'none'",
    "script-src 'self'",
    "style-src 'self' 'unsafe-inline'",
    "img-src 'self'",
    "font-src 'self'",
    "connect-src 'none'",
    "frame-src 'none'",
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'self'",
  ].join('; ');
  document.head.prepend(meta);
}
